import React, { useEffect, useState } from 'react';
import { learnerApi } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import Spinner from '../../components/common/Spinner';
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaSchool,
         FaCalendarAlt, FaIdCard, FaCamera, FaEdit, FaCheck, FaTimes } from 'react-icons/fa';
import toast from 'react-hot-toast';

export default function LearnerProfile() {
  const { user, school } = useAuth();
  const [profile,   setProfile]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [editing,   setEditing]   = useState(false);
  const [phone,     setPhone]     = useState('');
  const [saving,    setSaving]    = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview,   setPreview]   = useState(null);

  const primary = school?.primary_color || '#0d6efd';

  const load = () => {
    learnerApi.getProfile()
      .then(r => {
        setProfile(r.data.data);
        setPhone(r.data.data.phone || '');
        setPreview(r.data.data.avatar);
      })
      .catch(() => toast.error('Failed to load profile'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await learnerApi.updateProfile({ phone });
      toast.success('Profile updated');
      setEditing(false);
      load();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    const fd = new FormData();
    fd.append('avatar', file);
    const token = localStorage.getItem('access_token');
    if (token) fd.append('_token', token);
    try {
      const res = await learnerApi.uploadAvatar(fd);
      const url = res.data.data.avatar_url;
      setPreview(url);
      // Update sidebar avatar
      const updated = { ...user, avatar: url };
      localStorage.setItem('user', JSON.stringify(updated));
      window.dispatchEvent(new Event('storage'));
      toast.success('Photo updated');
    } catch { toast.error('Failed to upload photo'); setPreview(profile?.avatar); }
    finally { setUploading(false); }
  };

  if (loading) return <Spinner />;
  if (!profile) return <div className="sms-card" style={{ textAlign: 'center', color: '#9ca3af', padding: '60px 0' }}>Profile not found</div>;

  const age = profile.dob ? new Date().getFullYear() - new Date(profile.dob).getFullYear() : null;
  const enrolledYear = profile.created_at ? new Date(profile.created_at).getFullYear() : '—';

  return (
    <div>
      <div className="page-header"><h2>My Profile</h2><p>Your personal information and account details</p></div>

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 24, alignItems: 'start' }}>

        {/* Left — avatar + school card */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Avatar card */}
          <div className="sms-card" style={{ textAlign: 'center', padding: 32 }}>
            {/* Avatar */}
            <div style={{ position: 'relative', display: 'inline-block', marginBottom: 16 }}>
              <div style={{
                width: 120, height: 120, borderRadius: '50%',
                background: preview ? 'transparent' : `${primary}22`,
                border: `4px solid ${primary}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', margin: '0 auto',
                boxShadow: `0 4px 20px ${primary}33`,
              }}>
                {preview
                  ? <img src={preview} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <FaUser size={48} color={primary} />
                }
              </div>
              {/* Upload overlay */}
              <label style={{
                position: 'absolute', bottom: 4, right: 4,
                width: 34, height: 34, borderRadius: '50%',
                background: primary, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                transition: 'transform 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
                <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleAvatarChange} />
                {uploading ? <div style={{ width: 14, height: 14, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                           : <FaCamera size={14} color="#fff" />}
              </label>
            </div>

            <div style={{ fontWeight: 800, fontSize: '1.25rem', color: '#111827' }}>
              {profile.first_name} {profile.surname}
            </div>
            <div style={{ color: primary, fontWeight: 600, fontSize: '0.9rem', marginTop: 4 }}>
              Grade {profile.grade_number || '—'}
              {profile.grade_name ? ` — ${profile.grade_name}` : ''}
            </div>
            {age && <div style={{ color: '#6b7280', fontSize: '0.82rem', marginTop: 2 }}>{age} years old</div>}

            {/* Quick stats */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 20 }}>
              {[
                { label: 'Days Present',    value: profile.days_present,     color: '#10b981' },
                { label: 'Assessments',      value: profile.assessments_done, color: primary },
              ].map(s => (
                <div key={s.label} style={{ padding: '12px 8px', background: `${s.color}11`, borderRadius: 10, border: `1px solid ${s.color}22` }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* School card */}
          <div className="sms-card">
            <h5 style={{ fontWeight: 700, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaSchool color={primary} /> My School
            </h5>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {profile.school_logo
                ? <img src={profile.school_logo} alt="logo" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover' }} />
                : <div style={{ width: 48, height: 48, borderRadius: 10, background: `${primary}22`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaSchool color={primary} size={22} />
                  </div>
              }
              <div>
                <div style={{ fontWeight: 700, color: '#111827', textTransform: 'capitalize' }}>{profile.school_name}</div>
                {profile.motto && <div style={{ color: '#6b7280', fontSize: '0.78rem', fontStyle: 'italic' }}>"{profile.motto}"</div>}
              </div>
            </div>
            <div style={{ marginTop: 12, color: '#6b7280', fontSize: '0.82rem' }}>
              Enrolled: {enrolledYear}
            </div>
          </div>
        </div>

        {/* Right — details */}
        <div className="sms-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h5 style={{ fontWeight: 700, margin: 0 }}>Personal Information</h5>
            {!editing
              ? <button onClick={() => setEditing(true)} className="sms-btn sms-btn-outline sms-btn-sm">
                  <FaEdit size={12} /> Edit
                </button>
              : <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={handleSave} disabled={saving} className="sms-btn sms-btn-primary sms-btn-sm" style={{ background: primary }}>
                    <FaCheck size={12} /> {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={() => { setEditing(false); setPhone(profile.phone || ''); }} className="sms-btn sms-btn-outline sms-btn-sm">
                    <FaTimes size={12} /> Cancel
                  </button>
                </div>
            }
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* Read-only fields */}
            {[
              { icon: FaUser,        label: 'First Name',   value: profile.first_name },
              { icon: FaUser,        label: 'Surname',      value: profile.surname },
              { icon: FaEnvelope,    label: 'Email',        value: profile.email || '—' },
              { icon: FaIdCard,      label: 'ID Number',    value: profile.id_number || '—' },
              { icon: FaCalendarAlt, label: 'Date of Birth',value: profile.dob || '—' },
              { icon: FaUser,        label: 'Gender',       value: profile.gender ? profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1) : '—' },
            ].map(f => (
              <div key={f.label} style={{ padding: '14px 16px', background: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                  <f.icon size={13} color={primary} />
                  <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{f.label}</span>
                </div>
                <div style={{ fontWeight: 500, color: '#111827', fontSize: '0.92rem' }}>{f.value}</div>
              </div>
            ))}

            {/* Editable: Phone */}
            <div style={{ padding: '14px 16px', background: editing ? `${primary}08` : '#f9fafb', borderRadius: 10, border: `1px solid ${editing ? primary + '44' : '#f3f4f6'}`, transition: 'all 0.2s' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                <FaPhone size={13} color={primary} />
                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Phone</span>
              </div>
              {editing
                ? <input className="sms-input" value={phone} onChange={e => setPhone(e.target.value)}
                    placeholder="e.g. +27 71 000 0000"
                    style={{ border: 'none', background: 'transparent', padding: '2px 0', fontSize: '0.92rem', outline: 'none', width: '100%' }} />
                : <div style={{ fontWeight: 500, color: '#111827', fontSize: '0.92rem' }}>{profile.phone || '—'}</div>
              }
            </div>

            {/* Address — full width */}
            <div style={{ gridColumn: '1/-1', padding: '14px 16px', background: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                <FaMapMarkerAlt size={13} color={primary} />
                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Address</span>
              </div>
              <div style={{ fontWeight: 500, color: '#111827', fontSize: '0.92rem' }}>{profile.address || '—'}</div>
            </div>
          </div>

          {/* Account info */}
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Account</div>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: '0.83rem', color: '#6b7280' }}>
              <span>Status: <strong style={{ color: '#10b981' }}>{profile.status}</strong></span>
              <span>Grade: <strong style={{ color: primary }}>Grade {profile.grade_number || '—'}</strong></span>
              <span>Member since: <strong>{new Date(profile.created_at).toLocaleDateString('en', { month: 'long', year: 'numeric' })}</strong></span>
            </div>
          </div>

          {/* Note about what admin can change */}
          <div style={{ marginTop: 16, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', fontSize: '0.8rem', color: '#1e40af' }}>
            ℹ️ Name, ID number, gender, DOB and address can only be updated by your school administrator.
            You can update your phone number and profile photo directly.
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
